/* eslint-disable react/display-name */
import dayjs from 'dayjs';
import {
  CANCEL_TYPES,
  INTEGRATION_NAME_MAP,
  ERROR,
  SUCCESS,
  DELIVERY_BY_RESTAURANT,
  DELIVERY,
} from 'constants/reporting';
import { Space } from 'antd';
import PropTypes from 'prop-types';
import { CANCELLED } from 'constants/cuboh';
import { DATE_MDY } from 'constants/dateTime';
import { ChowNowCaretDown, ChowNowCaretUp } from 'components/Cuboh/ChowNowIcon';
import { isSubstring } from 'helpers/validation';
import { exceptionOrderCodes } from 'helpers/reporting';

export const calculateSubsidizedDeliveryFee = meta => {
  if (!meta) {
    return Number(0);
  }
  const {
    total_delivery_fee_cents: totalDeliveryFeeCents,
    maximum_consumer_delivery_fee_cents: maximumConsumerDeliveryFeeCents,
    subsidized_delivery_fee_active: subsidizedDeliveryFeeActive,
  } = meta;
  if (subsidizedDeliveryFeeActive === 'False' || !maximumConsumerDeliveryFeeCents) {
    return 0;
  }

  return Number(totalDeliveryFeeCents) - Number(maximumConsumerDeliveryFeeCents);
};

export const normalizeOrderStatus = value => {
  if (!value) {
    return value;
  }

  const stringValue = String(value).toLowerCase().replace(/\s+/g, '_');

  if (stringValue.startsWith('cancel')) {
    return 'cancelled';
  }

  // Treat any "complete"/"completed"/"ready" style value as the COMPLETED state.
  if (stringValue.startsWith('complete') || stringValue.startsWith('ready')) {
    return 'completed';
  }

  // Treat any "accept"/"accepted" style value as the ACTIVE state so that
  // filters using the user-facing "Accepted" label still match ACTIVE rows.
  if (stringValue.startsWith('accept') || stringValue.startsWith('active')) {
    return 'active';
  }

  // Support both the API-style IN_PROGRESS and the UI label "In-progress".
  if (stringValue.startsWith('in_progress') || stringValue.startsWith('in-progress')) {
    return 'in_progress';
  }

  if (stringValue.startsWith('schedule')) {
    return 'scheduled';
  }

  if (stringValue.startsWith('inactive')) {
    return 'inactive';
  }

  return stringValue;
};

export const generateStatusTooltip = record => {
  if (!record) return null;

  const internalAppName = (record['APIOrder.app'] || '').toLowerCase();

  if (record?.['APIStatus.description'] === CANCELLED) {
    const tooltipText = 'This order has been canceled';

    switch (record['APIOrderCancellationData.cancelled_by']) {
      case CANCEL_TYPES.RESTAURANT:
        return `${tooltipText} by the restaurant`;
      case CANCEL_TYPES.DELIVERY_APP: {
        const app = INTEGRATION_NAME_MAP[internalAppName] || internalAppName;
        return `${tooltipText} by the ${app}`;
      }
      default:
        return tooltipText;
    }
  }

  return null;
};

// eslint-disable-next-line default-param-last
export const newFilterDataSource = (dataSource = [], customFilters, searchFilter) => {
  const { searchKey, searchValue } = searchFilter || {};

  const hasSearchValue =
    searchValue !== undefined && searchValue !== null && String(searchValue).trim() !== '';

  return dataSource.filter(row => {
    const passesCustomFilters = Object.entries(customFilters).every(([key, filterValues]) => {
      const rowValue = row[key];

      if (!rowValue || filterValues.length === 0) return true;

      if (key === 'APIStatus.description') {
        const normalizedRowValue = normalizeOrderStatus(rowValue);
        const normalizedFilterValues = filterValues.map(normalizeOrderStatus);

        return normalizedFilterValues.includes(normalizedRowValue);
      }

      if (Array.isArray(rowValue)) {
        return rowValue.some(value => filterValues.includes(value));
      }

      return filterValues.includes(rowValue);
    });

    const passesSearchFilter =
      !hasSearchValue ||
      (() => {
        const rowSearchValue = row[searchKey];

        if (!rowSearchValue) return false;
        if (typeof rowSearchValue === 'string') {
          return rowSearchValue.toLowerCase().includes(searchValue.toLowerCase());
        }
        if (Array.isArray(rowSearchValue)) {
          return rowSearchValue.some(value =>
            value.toLowerCase().includes(searchValue.toLowerCase()),
          );
        }
        return false;
      })();

    return passesCustomFilters && passesSearchFilter;
  });
};
/**
 * Apply custom filter state to the datasource and return valid rows
 * @param {object} dataSource: The menu item table datasource
 * @param {object} customFilters: The custom filter state for the table
 * @returns The datasource with custom filters applied
 */

export const filterDataSource = (dataSource, customFilters, posError) => {
  const { searchFilters } = customFilters;

  return (
    // Loop through filter fields object, and check substring match
    (dataSource ?? [])?.filter(row =>
      Object.keys(searchFilters).reduce((accumulator, filterField) => {
        /**
         * If the row does have the filter field, don't filter
         * It's a custom field and this IF block must be before the generic
         * filterField as the column doesn't exist at initial check
         */
        if (filterField === 'pos-error') {
          const exceptionsToMatch = exceptionOrderCodes(posError);
          if (
            exceptionsToMatch?.includes(row['APIOrder.code']) &&
            searchFilters[filterField].includes(ERROR)
          ) {
            return true;
          }
          if (
            !exceptionsToMatch?.includes(row['APIOrder.code']) &&
            searchFilters[filterField].includes(SUCCESS)
          ) {
            return true;
          }
          // When array is empty return all results
          if (searchFilters[filterField].length === 0) {
            return true;
          }

          return false;
        }

        if (!(filterField in row)) {
          return true;
        }

        // If an array, filter based on if the values exist in the search array
        if (typeof row[filterField] !== 'string') {
          const found = row[filterField].some(r => searchFilters[filterField].indexOf(r) >= 0);
          if (found) {
            return true;
          }
        }

        // Don't check array if value is a string
        if (typeof searchFilters[filterField] !== 'string') {
          // If Delivery filter active
          if (searchFilters[filterField].includes(DELIVERY)) {
            // Add delivery by restaurant if not in array
            if (!searchFilters[filterField].includes(DELIVERY_BY_RESTAURANT)) {
              searchFilters[filterField].push(DELIVERY_BY_RESTAURANT);
            }
          }

          // When array is empty return all results
          if (searchFilters[filterField].length === 0) {
            return true;
          }

          if (!searchFilters[filterField].includes(row[filterField])) {
            return false;
          }
        }

        // Don't check substring if the value is an array
        if (!Array.isArray(searchFilters[filterField])) {
          if (!isSubstring(row[filterField], searchFilters[filterField])) {
            return false;
          }
        }

        // Pass current value
        return accumulator;
      }, true),
    ) ?? []
  );
};

/* eslint-disable no-param-reassign */
export const exportDataFilter = (dataSource, selectedRowKeys, created) =>
  (dataSource ?? []).filter((row, i) => {
    row.date = dayjs(row[`${created}`]).format(DATE_MDY);
    row.ordertime = dayjs(row[`${created}`]).format('hh:mm:ss a');
    return selectedRowKeys.includes(i);
  });

export const mergeCustomFilter = (
  checked,
  customFilters,
  setCustomFilters,
  filterColumn,
  value,
) => {
  // Checked and array is undefined
  if (checked && !customFilters.searchFilters[filterColumn]) {
    setCustomFilters({
      ...customFilters,
      searchFilters: {
        ...customFilters.searchFilters,
        [filterColumn]: [value],
      },
    });
  }

  if (checked && customFilters.searchFilters[filterColumn]) {
    customFilters.searchFilters[filterColumn].push(value);
    setCustomFilters({
      ...customFilters,
    });
  }

  if (!checked && customFilters.searchFilters[filterColumn]) {
    // Tried a few methods to not due this assignment but they did not pan out.
    customFilters.searchFilters[filterColumn] =
      customFilters.searchFilters[filterColumn]?.filter(filterValue => filterValue !== value) ?? [];
    if (value === DELIVERY) {
      customFilters.searchFilters[filterColumn] =
        customFilters.searchFilters[filterColumn]?.filter(
          filterValue => filterValue !== DELIVERY_BY_RESTAURANT,
        ) ?? [];
    } else {
      setCustomFilters({
        ...customFilters,
      });
    }
  }
};

export const getIfChecked = (customFilters, filterColumn, value) => {
  if (customFilters.searchFilters[filterColumn]?.includes(value)) {
    return true;
  }
  return false;
};

export function SortIcon({ sortOrder }) {
  const ascendActive = sortOrder === 'ascend';
  const descendActive = sortOrder === 'descend';

  return (
    <Space size={0} direction="vertical" className="chownow__table__sorter__icons">
      <ChowNowCaretUp
        style={{ fontSize: 16 }}
        className={`chownow__table__sorter__icon chownow__table__sorter__icon--${
          ascendActive ? 'active' : 'inactive'
        }`}
      />
      <ChowNowCaretDown
        style={{ fontSize: 16 }}
        className={`chownow__table__sorter__icon chownow__table__sorter__icon--${
          descendActive ? 'active' : 'inactive'
        }`}
      />
    </Space>
  );
}
SortIcon.propTypes = {
  sortOrder: PropTypes.string,
};
